import { useState, useEffect } from "react";
import { MapPin, ChevronDown, Check, Search, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { ThemeToggle } from "@/components/ThemeToggle";

interface LocationItem {
  id: string;
  label: string;
}

interface LocationGroup {
  district: string;
  locations: LocationItem[];
}

const ncrDistricts: LocationGroup[] = [
  {
    district: "Capital District",
    locations: [
      { id: "manila", label: "Manila City" },
    ],
  },
  {
    district: "Eastern Manila",
    locations: [
      { id: "mandaluyong", label: "Mandaluyong" },
      { id: "marikina", label: "Marikina" },
      { id: "pasig", label: "Pasig" },
      { id: "quezon-city", label: "Quezon City" },
      { id: "san-juan", label: "San Juan" },
    ],
  },
  {
    district: "Northern Manila",
    locations: [
      { id: "caloocan", label: "Caloocan" },
      { id: "malabon", label: "Malabon" },
      { id: "navotas", label: "Navotas" },
      { id: "valenzuela", label: "Valenzuela" },
    ],
  },
  {
    district: "Southern Manila",
    locations: [
      { id: "las-pinas", label: "Las Piñas" },
      { id: "makati", label: "Makati" },
      { id: "muntinlupa", label: "Muntinlupa" },
      { id: "paranaque", label: "Parañaque" },
      { id: "pasay", label: "Pasay" },
      { id: "pateros", label: "Pateros" },
      { id: "taguig", label: "Taguig" },
    ],
  },
];

const allLocations = ncrDistricts.flatMap((g) => g.locations);

interface HeaderBarProps {
  userName?: string;
  isProvider?: boolean;
  searchQuery?: string;
  onSearch?: (query: string) => void;
}

/* ── Search Bar Component ── */
const SearchInput = ({ initialValue, onDebouncedSearch }: { initialValue: string, onDebouncedSearch: (q: string) => void }) => {
  const [localQuery, setLocalQuery] = useState(initialValue);
  const [searchFocused, setSearchFocused] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => onDebouncedSearch(localQuery), 300);
    return () => clearTimeout(timer);
  }, [localQuery, onDebouncedSearch]);

  return (
    <div className={`relative transition-all duration-200 rounded-[12px] overflow-hidden border ${searchFocused ? "border-emerald-accent shadow-[var(--input-focus-ring)]" : "border-[var(--glass-border)]"}`} style={{ background: "var(--input-bg)", backdropFilter: "blur(14px)" }}>
      <Search size={20} strokeWidth={1.5} className={`absolute left-3.5 top-1/2 -translate-y-1/2 transition-colors duration-200 ${searchFocused ? "text-emerald-accent" : "text-muted-foreground"}`} />
      <Input
        value={localQuery}
        onChange={(e) => setLocalQuery(e.target.value)}
        onFocus={() => setSearchFocused(true)}
        onBlur={() => setSearchFocused(false)}
        placeholder="Search services"
        className="pl-[38px] h-[40px] w-full bg-transparent border-none shadow-none text-[14px] font-medium text-foreground placeholder:text-muted-foreground pr-9 focus-visible:ring-0 focus-visible:border-none transition-all tracking-normal"
      />
      {localQuery && (
        <button
          onClick={() => setLocalQuery("")}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-black/10 dark:hover:bg-foreground/10 transition-colors press-scale"
        >
          <X size={16} strokeWidth={2} className="text-muted-foreground" />
        </button>
      )}
    </div>
  );
};
SearchInput.displayName = "SearchInput";

const HeaderBar = ({ userName, isProvider, searchQuery = "", onSearch }: HeaderBarProps) => {
  const [activeLocation, setActiveLocation] = useState(allLocations[0]);


  return (
    <header className="glass-header text-foreground pt-6 pb-3 shrink-0 transition-colors duration-200 sticky top-0 z-40 rounded-b-[20px]">
      <div className="px-5">
        <div className="flex items-center gap-3 w-full">
          {/* Integrated Search Bar */}
          <div className="flex-1">
            <SearchInput
              initialValue={searchQuery}
              onDebouncedSearch={(q) => onSearch && onSearch(q)}
            />
          </div>

          {/* Location Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1 shrink-0 press-scale transition-all max-w-[160px] sm:max-w-[180px] justify-end">
                <MapPin size={18} className="text-foreground shrink-0" />
                <span className="text-[13px] font-bold text-foreground truncate text-right" title={activeLocation.label}>
                  {activeLocation.label}
                </span>
                <ChevronDown size={16} className="opacity-80 shrink-0 text-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 max-h-72 overflow-y-auto mt-2">
              {ncrDistricts.map((group, gi) => (
                <div key={group.district}>
                  {gi > 0 && <Separator className="my-1" />}
                  <p className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    {group.district}
                  </p>
                  {group.locations.map((loc) => (
                    <DropdownMenuItem
                      key={loc.id}
                      onClick={() => setActiveLocation(loc)}
                      className="flex items-center justify-between text-sm cursor-pointer text-foreground"
                    >
                      <span className="flex items-center gap-2">
                        <MapPin size={15} className="text-muted-foreground" />
                        {loc.label}
                      </span>
                      {activeLocation.id === loc.id && <Check size={16} className="text-emerald-accent" />}
                    </DropdownMenuItem>
                  ))}
                </div>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Theme Toggle */}
          <ThemeToggle />
        </div>
      </div>
    </header>
  );
};

export default HeaderBar;
